<script setup>
import { computed, onUnmounted, ref, nextTick, watch } from 'vue';
import { useTimeoutPoll } from '@vueuse/core';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { debounce } from '@chatwoot/utils';

import DeleteDialog from 'dashboard/components-next/polaris/pageComponents/DeleteDialog.vue';
import DocumentCardSkin from 'dashboard/components-next/polaris/skin/DocumentCardSkin.vue';
import DocumentFilter from 'dashboard/components-next/polaris/assistant/DocumentFilter.vue';
import DocumentBulkActions from 'dashboard/components-next/polaris/assistant/DocumentBulkActions.vue';
import Policy from 'dashboard/components/policy.vue';
import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import DocumentDetails from 'dashboard/components-next/polaris/pageComponents/document/DocumentDetails.vue';
import DocumentAssistantAccess from 'dashboard/components-next/polaris/pageComponents/document/DocumentAssistantAccess.vue';
import CreateDocumentDialog from 'dashboard/components-next/polaris/pageComponents/document/CreateDocumentDialog.vue';
import DocumentPageEmptyState from 'dashboard/components-next/polaris/pageComponents/emptyStates/DocumentPageEmptyState.vue';
import PolarisEmptyState from 'dashboard/components-next/polaris/pageComponents/emptyStates/PolarisEmptyState.vue';
import PolarisDocumentAPI from 'dashboard/api/polaris/document';
import { useI18n } from 'vue-i18n';

const route = useRoute();
const store = useStore();
const { t } = useI18n();
const { checkPermissions } = usePolicy();

const SYNC_POLL_INTERVAL_MS = 5000;
const SYNC_POLL_MAX_DURATION_MS = 15 * 60 * 1000;

const uiFlags = useMapGetter('polarisDocuments/getUIFlags');
const documents = useMapGetter('polarisDocuments/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const documentsMeta = useMapGetter('polarisDocuments/getMeta');
const accountAssistants = useMapGetter('polarisAssistants/getRecords');

const selectedAssistantId = computed(() => Number(route.params.assistantId));
const canManageDocuments = computed(() => checkPermissions(['administrator']));

const selectedDocument = ref(null);
const deleteDocumentDialog = ref(null);
const bulkSelectedIds = ref(new Set());

const handleDelete = () => {
  deleteDocumentDialog.value.dialogRef.open();
};

// The document whose details are open in the drawer (its own ref: a delete or sync on another card, while the
// drawer is open, must not swap what the drawer shows).
const detailsDocument = ref(null);
// Open or closed; the document stays in `detailsDocument` while the drawer closes (its focus goes back to the card).
const detailsOpen = ref(false);
const showCreateDialog = ref(false);
const createDocumentDialog = ref(null);

const handleShowDocumentDetails = () => {
  detailsDocument.value = selectedDocument.value;
  detailsOpen.value = true;
};
const handleCreateDocument = () => {
  showCreateDialog.value = true;
  nextTick(() => createDocumentDialog.value.dialogRef.open());
};

const handleDocumentDetailsClose = () => {
  detailsOpen.value = false;
};

const handleCreateDialogClose = () => {
  showCreateDialog.value = false;
};

const documentFilter = ref(null);
const syncIntervalHours = ref(null);
const searchQuery = ref('');

const buildDocumentFilterParams = (page = 1) => {
  const filterParams = documentFilter.value?.buildParams(page) ?? {
    page,
    sort: 'recently_updated',
  };
  // Conhecimento por conta (Fase 6): a lista é da conta inteira — o assistente da rota só define ONDE um
  // documento novo nasce (CreateDocumentDialog). "Quem pode usar" se edita na linha, pela junção.
  const trimmedQuery = searchQuery.value.trim();
  if (trimmedQuery) filterParams.searchKey = trimmedQuery;
  return filterParams;
};

let documentsRequestId = 0;
let fetchingListRequestId = null;

const isCurrentDocumentRequest = requestId => requestId === documentsRequestId;

const pruneSelectionToDocuments = nextDocuments => {
  if (!bulkSelectedIds.value.size) return;

  const visibleDocumentIds = new Set(nextDocuments.map(doc => doc.id));
  const selectedIds = new Set(
    [...bulkSelectedIds.value].filter(id => visibleDocumentIds.has(id))
  );

  if (selectedIds.size !== bulkSelectedIds.value.size) {
    bulkSelectedIds.value = selectedIds;
  }
};

const fetchDocuments = async (page = 1, { showLoader = true } = {}) => {
  documentsRequestId += 1;
  const requestId = documentsRequestId;
  const filterParams = buildDocumentFilterParams(page);

  if (showLoader) {
    fetchingListRequestId = requestId;
    store.dispatch('polarisDocuments/setFetchingList', true);
  }

  try {
    const response = await PolarisDocumentAPI.get(filterParams);

    if (!isCurrentDocumentRequest(requestId)) {
      return [];
    }

    const { payload, meta } = response.data;
    store.dispatch('polarisDocuments/setRecords', { records: payload, meta });
    pruneSelectionToDocuments(payload);
    syncIntervalHours.value = Number(meta?.sync_interval_hours) || null;
    return payload;
  } catch (error) {
    if (isCurrentDocumentRequest(requestId)) {
      throw error;
    }
    return [];
  } finally {
    if (showLoader && fetchingListRequestId === requestId) {
      fetchingListRequestId = null;
      store.dispatch('polarisDocuments/setFetchingList', false);
    }
  }
};

const refreshDocumentsPage = (
  page = documentsMeta.value?.page || 1,
  { showLoader = false } = {}
) => {
  return fetchDocuments(page, { showLoader }).catch(() => {});
};

const onFiltersChanged = () => {
  bulkSelectedIds.value = new Set();
  fetchDocuments(1);
};

const debouncedSearch = debounce(() => {
  bulkSelectedIds.value = new Set();
  fetchDocuments(1);
}, 300);

const syncPollStartedAt = ref(null);

const hasDocumentsSyncing = computed(() =>
  (documents.value || []).some(doc => doc.sync_in_progress)
);

const hasSyncingDocuments = computed(() => hasDocumentsSyncing.value);

const isWithinSyncPollWindow = () =>
  syncPollStartedAt.value &&
  Date.now() - syncPollStartedAt.value < SYNC_POLL_MAX_DURATION_MS;

const shouldContinueSyncPolling = () =>
  hasSyncingDocuments.value && isWithinSyncPollWindow();

let syncPollingControls;

function stopSyncPolling() {
  syncPollingControls.pause();
  syncPollStartedAt.value = null;
}

async function pollSyncDocuments() {
  try {
    await refreshDocumentsPage();
  } catch (error) {
    // Keep the existing polling decision based on the last known sync state.
  }

  if (!shouldContinueSyncPolling()) {
    stopSyncPolling();
  }
}

function scheduleSyncPoll({ extendWindow = false } = {}) {
  if (extendWindow || !syncPollStartedAt.value) {
    syncPollStartedAt.value = Date.now();
  }

  if (syncPollingControls.isActive.value) return;
  syncPollingControls.resume();
}

syncPollingControls = useTimeoutPoll(pollSyncDocuments, SYNC_POLL_INTERVAL_MS, {
  immediate: false,
});

watch(hasSyncingDocuments, isSyncing => {
  if (isSyncing) {
    scheduleSyncPoll();
  }
});

const handleSync = async id => {
  try {
    await store.dispatch('polarisDocuments/sync', id);
    useAlert(t('POLARIS.DOCUMENTS.SYNC.QUEUED_MESSAGE'));
    scheduleSyncPoll({ extendWindow: true });
  } catch (error) {
    useAlert(t('POLARIS.DOCUMENTS.SYNC.ERROR_MESSAGE'));
  }
};

// "Quem pode usar" (Fase 6): grava na junção via store; em erro, recarrega a página para desfazer o checkbox.
const handleToggleAssistant = async (documentId, { assistantId, allowed }) => {
  try {
    await store.dispatch('polarisDocuments/setAssistantAccess', {
      id: documentId,
      assistantId,
      allowed,
    });
  } catch (error) {
    useAlert(t('POLARIS.DOCUMENTS.SHARING.ERROR_MESSAGE'));
    refreshDocumentsPage();
  }
};

const handleAction = ({ action, id }) => {
  selectedDocument.value = documents.value.find(
    polarisDocument => id === polarisDocument.id
  );

  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    } else if (action === 'viewDetails') {
      handleShowDocumentDetails();
    } else if (action === 'sync') {
      handleSync(id);
    }
  });
};

const onPageChange = page => {
  const hadSelection = bulkSelectedIds.value.size > 0;
  fetchDocuments(page);

  if (hadSelection) {
    bulkSelectedIds.value = new Set();
  }
};

const onDeleteSuccess = () => {
  if (detailsDocument.value?.id === selectedDocument.value?.id) {
    detailsOpen.value = false;
  }
  if (documents.value?.length === 0 && documentsMeta.value?.page > 1) {
    onPageChange(documentsMeta.value.page - 1);
  } else {
    refreshDocumentsPage();
  }
};

const handleCardSelect = id => {
  if (!canManageDocuments.value) return;
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const fetchDocumentsAfterBulkAction = () => {
  const hasNoDocumentsLeft = documents.value?.length === 0;
  const currentPage = documentsMeta.value?.page;

  if (hasNoDocumentsLeft) {
    const pageToFetch = currentPage > 1 ? currentPage - 1 : currentPage;
    fetchDocuments(pageToFetch);
  } else {
    fetchDocuments(currentPage);
  }

  bulkSelectedIds.value = new Set();
};

const onCreateSuccess = () => {
  refreshDocumentsPage(1);
};

const hasActiveDocumentFilters = computed(
  () =>
    (documentFilter.value?.hasActiveFilters ?? false) ||
    Boolean(searchQuery.value.trim())
);

watch(
  selectedAssistantId,
  async () => {
    documentFilter.value?.reset();
    detailsOpen.value = false;
    searchQuery.value = '';
    bulkSelectedIds.value = new Set();
    syncIntervalHours.value = null;
    stopSyncPolling();
    store.dispatch('polarisAssistants/get', { page: 1 });
    await fetchDocuments(1);
    if (hasSyncingDocuments.value) scheduleSyncPoll();
  },
  { immediate: true }
);

onUnmounted(() => {
  stopSyncPolling();
  documentsRequestId += 1;
});
</script>

<template>
  <PageLayout
    preview="open"
    :header-title="$t('POLARIS.DOCUMENTS.HEADER')"
    :button-label="$t('POLARIS.DOCUMENTS.ADD_NEW')"
    :button-policy="['administrator']"
    :total-count="documentsMeta.totalCount"
    :current-page="documentsMeta.page"
    :show-pagination-footer="!isFetching && !!documents.length"
    :is-fetching="isFetching"
    :is-empty="!documents.length && !hasActiveDocumentFilters"
    :feature-flag="FEATURE_FLAGS.POLARIS"
    :invitation="{
      title: $t('POLARIS.DOCUMENTS.EMPTY_STATE.FEATURE_SPOTLIGHT.TITLE'),
      text: $t('POLARIS.DOCUMENTS.EMPTY_STATE.FEATURE_SPOTLIGHT.NOTE'),
      actionLabel: $t('POLARIS.DOCUMENTS.ADD_NEW'),
    }"
    invitation-key="documents"
    @update:current-page="onPageChange"
    @click="handleCreateDocument"
  >
    <template #subHeader>
      <div class="grid gap-3">
        <Policy :permissions="['administrator']">
          <DocumentBulkActions
            v-model:selected-ids="bulkSelectedIds"
            :documents="documents"
            @bulk-sync-queued="scheduleSyncPoll({ extendWindow: true })"
            @bulk-delete-succeeded="fetchDocumentsAfterBulkAction"
          />
        </Policy>
        <DocumentFilter
          v-show="!bulkSelectedIds.size"
          ref="documentFilter"
          v-model:search="searchQuery"
          :count="documents.length ? documentsMeta.totalCount : null"
          @update:search="debouncedSearch"
          @change="onFiltersChanged"
        />
      </div>
    </template>

    <template #emptyState>
      <DocumentPageEmptyState @click="handleCreateDocument" />
    </template>

    <template #paywall />

    <template #body>
      <PolarisEmptyState
        v-if="!documents.length && hasActiveDocumentFilters"
        :title="$t('POLARIS.DOCUMENTS.EMPTY_STATE.FILTERED_TITLE')"
        :description="$t('POLARIS.DOCUMENTS.EMPTY_STATE.FILTERED_SUBTITLE')"
      />

      <div v-else class="flex flex-col gap-4">
        <div v-for="doc in documents" :key="doc.id" class="flex flex-col gap-1">
          <DocumentCardSkin
            :polaris-document="doc"
            :selectable="canManageDocuments"
            :selected="canManageDocuments && bulkSelectedIds.has(doc.id)"
            @action="handleAction"
            @select="handleCardSelect"
          />
          <DocumentAssistantAccess
            :polaris-document="doc"
            :assistants="accountAssistants"
            :disabled="!canManageDocuments"
            @toggle="handleToggleAssistant(doc.id, $event)"
          />
        </div>
      </div>
    </template>

    <CreateDocumentDialog
      v-if="showCreateDialog"
      ref="createDocumentDialog"
      :assistant-id="selectedAssistantId"
      @create-success="onCreateSuccess"
      @close="handleCreateDialogClose"
    />
    <DeleteDialog
      v-if="selectedDocument"
      ref="deleteDocumentDialog"
      :entity="selectedDocument"
      type="Documents"
      @delete-success="onDeleteSuccess"
    />
    <template #aside>
      <DocumentDetails
        :open="detailsOpen"
        :polaris-document="detailsDocument"
        @close="handleDocumentDetailsClose"
      />
    </template>
  </PageLayout>
</template>
